/** QuickHouse — Machines à états des outils 2D (select, wall, door/window, room, delete) */
import {
  dist, snapToGrid, snapToPoints, axisAlign, roundCoord, fmtLen,
  hitWall, projectOnWall, projectU, wallLength, pointOnWall, distPointToSegment,
} from '../../shared/geometry.js';

// Arrondi du modèle : précision 1 mm (0,1 cm)
const R = roundCoord;
import { openingCovered } from '../../shared/plan3d.js';
import { newId, openingDefaults } from '../../shared/schema.js';
import {
  pointInRoom, roomCentroid, bulgeHandles, bulgeFromPoint,
  roomHandles, resizeRectBlock, insertVertexNear,
} from '../../shared/rooms.js';
import { hitStair, translateStair } from '../../shared/stairs.js';
import { hitBeam, translateBeam, beamOrigin } from '../../shared/beams.js';
import { findCatalogItem } from '../../shared/catalog.js';
import { useProjectStore } from '../store/useProjectStore.js';

/** Le point est-il dans l'empreinte (tournée) d'un objet posé ? */
function pointInItem(item, entry, p) {
  const a = (-(item.rotation ?? 0) * Math.PI) / 180;
  const dx = p.x - item.position.x;
  const dy = p.y - item.position.y;
  const lx = dx * Math.cos(a) - dy * Math.sin(a);
  const ly = dx * Math.sin(a) + dy * Math.cos(a);
  const s = item.scale ?? 1;
  return Math.abs(lx) <= (entry.size.w * s) / 2 && Math.abs(ly) <= (entry.size.d * s) / 2;
}

/**
 * Accrochage des points de pièce : coins de pièces, extrémités de murs,
 * alignements H/V (avec guides), puis grille. Retourne { p, guides }.
 */
function snapRoomPoint(world, floor, settings, tol, excludeRoomId = null) {
  const candidates = [];
  for (const room of floor.rooms) {
    if (room.id === excludeRoomId) continue;
    for (const h of roomHandles(room)) candidates.push(h.p);
  }
  for (const w of floor.walls) candidates.push(w.a, w.b);
  const exact = snapToPoints(world, candidates, tol);
  if (exact) return { p: exact, guides: [], snapped: true };
  let gx = null;
  let gy = null;
  let bestDx = tol;
  let bestDy = tol;
  for (const c of candidates) {
    const ddx = Math.abs(world.x - c.x);
    if (ddx < bestDx) { bestDx = ddx; gx = c; }
    const ddy = Math.abs(world.y - c.y);
    if (ddy < bestDy) { bestDy = ddy; gy = c; }
  }
  const guides = [];
  if (gx) guides.push({ type: 'v', x: gx.x, ref: gx });
  if (gy) guides.push({ type: 'h', y: gy.y, ref: gy });
  let p = { x: gx ? gx.x : world.x, y: gy ? gy.y : world.y };
  if (settings.grid.snap) {
    const g = snapToGrid(p, settings.grid.size);
    p = { x: gx ? p.x : g.x, y: gy ? p.y : g.y };
  }
  return { p, guides, snapped: false };
}

// (les dimensions par défaut par type/variante viennent de shared/schema.js : openingDefaults)

/** Tous les points d'extrémité des murs d'un étage */
function endpoints(floor) {
  const pts = [];
  for (const w of floor.walls) { pts.push(w.a, w.b); }
  return pts;
}

/** Point snappé : extrémités → alignement axial → grille */
function snapPoint(world, floor, settings, tol, start = null, shift = false) {
  const pt = snapToPoints(world, endpoints(floor), tol);
  if (pt) return { p: pt, kind: 'endpoint' };
  let p = world;
  if (start) {
    const aligned = axisAlign(start, p, tol, shift);
    if (aligned) p = aligned;
  }
  if (settings.grid.snap) p = snapToGrid(p, settings.grid.size);
  return { p, kind: 'grid' };
}

/** Hit-test généraliste : ouverture → mur → étiquette */
function hitAny(floor, world, tol) {
  const wallsById = new Map(floor.walls.map((w) => [w.id, w]));
  for (const op of floor.openings) {
    if (op.hidden) continue;
    // Velux : rectangle autonome sur le plan
    if (!op.wallId) {
      if (op.position
        && Math.abs(world.x - op.position.x) <= op.width / 2 + tol
        && Math.abs(world.y - op.position.y) <= op.height / 2 + tol) {
        return { kind: 'opening', id: op.id, opening: op, wall: null };
      }
      continue;
    }
    const wall = wallsById.get(op.wallId);
    if (!wall || wall.hidden) continue;
    const proj = projectOnWall(wall, world);
    if (proj.dist <= wall.thickness / 2 + tol && Math.abs(proj.u - op.offset) <= op.width / 2 + tol) {
      return { kind: 'opening', id: op.id, opening: op, wall };
    }
  }
  const hw = hitWall(floor.walls.filter((w) => !w.hidden), world, tol);
  if (hw) return { kind: 'wall', id: hw.wall.id, wall: hw.wall, u: hw.u };
  const catalog = useProjectStore.getState().catalog ?? [];
  for (const it of floor.items ?? []) {
    if (it.hidden) continue;
    const entry = findCatalogItem(catalog, it.catalogId);
    if (entry && pointInItem(it, entry, world)) return { kind: 'item', id: it.id, item: it };
  }
  for (const stair of floor.stairs ?? []) {
    if (!stair.hidden && hitStair(stair, world, tol) !== null) {
      return { kind: 'stair', id: stair.id, stair };
    }
  }
  for (const beam of floor.beams ?? []) {
    if (!beam.hidden && hitBeam(beam, world, tol)) {
      return { kind: 'beam', id: beam.id, beam };
    }
  }
  for (const dim of floor.dims ?? []) {
    if (!dim.hidden && distPointToSegment(world, dim.a, dim.b) <= tol) {
      return { kind: 'dim', id: dim.id, dim };
    }
  }
  for (const room of floor.rooms) {
    if (room.hidden) continue;
    if (dist(world, room.position) <= tol * 3 || pointInRoom(room, world)) {
      return { kind: 'room', id: room.id, room };
    }
  }
  return null;
}

/** Déplace une pièce entière (étiquette + blocs) */
function translateRoom(room, dx, dy) {
  room.position = { x: room.position.x + dx, y: room.position.y + dy };
  for (const block of room.blocks ?? []) {
    if (block.type === 'rect') {
      block.x += dx;
      block.y += dy;
    } else {
      for (const p of block.points) { p.x += dx; p.y += dy; }
    }
  }
}

/** Arrondit une pièce à la grille */
function snapRoomToGrid(room, size) {
  const r = (v) => R(Math.round(v / size) * size);
  for (const block of room.blocks ?? []) {
    if (block.type === 'rect') {
      block.x = r(block.x);
      block.y = r(block.y);
    } else {
      for (const p of block.points) { p.x = r(p.x); p.y = r(p.y); }
    }
  }
  room.position = { x: R(room.position.x), y: R(room.position.y) };
}

// ── Sélection multiple : helpers ──────────────────────────────
const sameSel = (p, q) => p.kind === q.kind && p.id === q.id;

/** Éléments contenus dans le rectangle (murs : les 2 extrémités, ouvertures : le centre) */
function itemsInRect(floor, a, b) {
  const x0 = Math.min(a.x, b.x), x1 = Math.max(a.x, b.x);
  const y0 = Math.min(a.y, b.y), y1 = Math.max(a.y, b.y);
  const inside = (p) => p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1;
  const out = [];
  const wallsById = new Map(floor.walls.map((w) => [w.id, w]));
  for (const w of floor.walls) {
    if (!w.hidden && inside(w.a) && inside(w.b)) out.push({ kind: 'wall', id: w.id });
  }
  for (const o of floor.openings) {
    if (o.hidden) continue;
    const wall = wallsById.get(o.wallId);
    if (wall && !wall.hidden && inside(pointOnWall(wall, o.offset))) out.push({ kind: 'opening', id: o.id });
  }
  for (const r of floor.rooms) {
    if (!r.hidden && inside(r.position)) out.push({ kind: 'room', id: r.id });
  }
  for (const s of floor.stairs ?? []) {
    if (s.hidden) continue;
    const anchor = s.kind === 'spiral' ? s.center : s.path[0];
    if (inside(anchor)) out.push({ kind: 'stair', id: s.id });
  }
  for (const it of floor.items ?? []) {
    if (!it.hidden && inside(it.position)) out.push({ kind: 'item', id: it.id });
  }
  for (const bm of floor.beams ?? []) {
    if (!bm.hidden && inside(beamOrigin(bm))) out.push({ kind: 'beam', id: bm.id });
  }
  return out;
}

/** Supprime tous les éléments sélectionnés (les murs emportent leurs ouvertures) */
export function deleteSelection(store, floorId) {
  const sel = store.getState().selection;
  if (!sel.length) return;
  store.getState().mutate((floors) => {
    const f = floors.find((x) => x.id === floorId);
    if (!f) return;
    const wallIds = new Set(sel.filter((s) => s.kind === 'wall').map((s) => s.id));
    const openingIds = new Set(sel.filter((s) => s.kind === 'opening').map((s) => s.id));
    const roomIds = new Set(sel.filter((s) => s.kind === 'room').map((s) => s.id));
    const stairIds = new Set(sel.filter((s) => s.kind === 'stair').map((s) => s.id));
    const itemIds = new Set(sel.filter((s) => s.kind === 'item').map((s) => s.id));
    const dimIds = new Set(sel.filter((s) => s.kind === 'dim').map((s) => s.id));
    const beamIds = new Set(sel.filter((s) => s.kind === 'beam').map((s) => s.id));
    f.walls = f.walls.filter((w) => !wallIds.has(w.id));
    f.openings = f.openings.filter((o) => !openingIds.has(o.id) && !(o.wallId && wallIds.has(o.wallId)));
    f.rooms = f.rooms.filter((r) => !roomIds.has(r.id));
    f.stairs = (f.stairs ?? []).filter((s) => !stairIds.has(s.id));
    f.items = (f.items ?? []).filter((it) => !itemIds.has(it.id));
    f.dims = (f.dims ?? []).filter((d) => !dimIds.has(d.id));
    f.beams = (f.beams ?? []).filter((b) => !beamIds.has(b.id));
    detachOrphans(f);
  });
  store.getState().setSelection([]);
  store.getState().setStatus('');
}

// ── Création / extension de pièce ou de dalle ─────────────────
/** Ajoute un bloc : à la pièce/dalle (même kind) contenant `anchor` si elle existe, sinon nouvelle */
function commitBlock(store, floor, block, anchor, kind = 'room') {
  const host = floor.rooms.find((r) => (r.kind ?? 'room') === kind && r.blocks?.length && pointInRoom(r, anchor));
  if (host) {
    store.getState().mutate((floors) => {
      const f = floors.find((x) => x.id === floor.id);
      const room = f.rooms.find((r) => r.id === host.id);
      room.blocks.push(block);
      const c = roomCentroid(room);
      if (c) room.position = { x: R(c.x), y: R(c.y) };
    });
    store.getState().setSelection([{ kind: 'room', id: host.id }]);
    return;
  }
  const label = { slab: ['Nom de la dalle ?', 'Dalle'], roof: ['Nom du toit ?', 'Toit'], room: ['Nom de la pièce ?', 'Pièce'] }[kind];
  const name = window.prompt(label[0], label[1]) ?? '';
  if (!name.trim()) return;
  const id = newId('r');
  store.getState().mutate((floors) => {
    const f = floors.find((x) => x.id === floor.id);
    const room = {
      id, name: name.trim(), kind, position: { x: 0, y: 0 }, blocks: [block],
      ...(kind === 'slab' && { thickness: null, material: 'concrete', elevation: 0 }),
      ...(kind === 'roof' && { material: 'slate', slope: 30, slopeDir: 'S', elevation: floor.height }),
    };
    const c = roomCentroid(room);
    room.position = c ? { x: R(c.x), y: R(c.y) } : { x: 0, y: 0 };
    f.rooms.push(room);
  });
  store.getState().setSelection([{ kind: 'room', id }]);
}

function closePoly(ctx, kind = 'room') {
  const { store, floor, ts, setUi } = ctx;
  const points = (ts.polyPoints ?? [])
    .filter((p, i, arr) => i === 0 || dist(p, arr[i - 1]) > 1) // doublons du double-clic
    .map((p) => ({ x: p.x, y: p.y, bulge: 0 }));
  ts.polyPoints = null;
  setUi({ preview: null, guides: null, status: '' });
  if (points.length >= 3) {
    commitBlock(store, floor, { type: 'poly', points }, points[0], kind);
  }
}

// ── Hiérarchie parent/enfant ──────────────────────────────────
/** Origine d'un élément : point de référence des positions relatives */
export function itemOrigin(floor, id) {
  const w = floor.walls.find((x) => x.id === id);
  if (w) return w.a;
  const o = floor.openings.find((x) => x.id === id);
  if (o) {
    if (o.position) return o.position;
    const wall = floor.walls.find((x) => x.id === o.wallId);
    return wall ? pointOnWall(wall, o.offset) : null;
  }
  const r = floor.rooms.find((x) => x.id === id);
  if (r) return r.position;
  const s = (floor.stairs ?? []).find((x) => x.id === id);
  if (s) return s.kind === 'spiral' ? s.center : s.path[0];
  const it = (floor.items ?? []).find((x) => x.id === id);
  if (it) return it.position;
  const dm = (floor.dims ?? []).find((x) => x.id === id);
  if (dm) return dm.a;
  const bm = (floor.beams ?? []).find((x) => x.id === id);
  if (bm) return beamOrigin(bm);
  return null;
}

/** Translate un élément seul (les ouvertures sur mur suivent leur mur : no-op) */
function translateItem(f, id, dx, dy) {
  const w = f.walls.find((x) => x.id === id);
  if (w) {
    w.a = { x: w.a.x + dx, y: w.a.y + dy };
    w.b = { x: w.b.x + dx, y: w.b.y + dy };
    return;
  }
  const o = f.openings.find((x) => x.id === id);
  if (o) {
    if (o.position) o.position = { x: o.position.x + dx, y: o.position.y + dy };
    return;
  }
  const r = f.rooms.find((x) => x.id === id);
  if (r) { translateRoom(r, dx, dy); return; }
  const s = (f.stairs ?? []).find((x) => x.id === id);
  if (s) { translateStair(s, dx, dy); return; }
  const it = (f.items ?? []).find((x) => x.id === id);
  if (it) { it.position = { x: it.position.x + dx, y: it.position.y + dy }; return; }
  const dm = (f.dims ?? []).find((x) => x.id === id);
  if (dm) {
    dm.a = { x: dm.a.x + dx, y: dm.a.y + dy };
    dm.b = { x: dm.b.x + dx, y: dm.b.y + dy };
    return;
  }
  const bm = (f.beams ?? []).find((x) => x.id === id);
  if (bm) translateBeam(bm, dx, dy);
}

/** Translate récursivement les DESCENDANTS de `id` (la position relative reste constante) */
export function translateDescendants(f, id, dx, dy, moved = new Set([id])) {
  const all = [...f.walls, ...f.openings, ...f.rooms, ...(f.stairs ?? []), ...(f.items ?? []), ...(f.beams ?? []), ...(f.dims ?? [])];
  for (const it of all) {
    if (it.parentId === id && !moved.has(it.id)) {
      moved.add(it.id);
      translateItem(f, it.id, dx, dy);
      translateDescendants(f, it.id, dx, dy, moved);
    }
  }
}

/** Translate TOUT l'étage de (−ox, −oy) : chaque élément est décalé une fois via
 *  translateItem (murs, ouvertures, pièces/dalles/toits, escaliers, objets, poutres,
 *  cotes) — de sorte que le point (ox, oy) devienne le nouveau (0, 0). Réutilise la
 *  logique de translation maison, donc rien n'est oublié ni décalé deux fois. */
export function shiftFloorCoords(f, ox, oy) {
  const dx = -ox;
  const dy = -oy;
  const all = [...f.walls, ...f.openings, ...f.rooms, ...(f.stairs ?? []), ...(f.items ?? []), ...(f.beams ?? []), ...(f.dims ?? [])];
  for (const it of all) translateItem(f, it.id, dx, dy);
}

/** Détache les enfants dont le parent n'existe plus */
function detachOrphans(f) {
  const ids = new Set([...f.walls, ...f.openings, ...f.rooms, ...(f.stairs ?? []), ...(f.items ?? []), ...(f.beams ?? []), ...(f.dims ?? [])].map((x) => x.id));
  for (const list of [f.walls, f.openings, f.rooms, f.stairs ?? [], f.items ?? [], f.beams ?? [], f.dims ?? []]) {
    for (const it of list) {
      if (it.parentId && !ids.has(it.parentId)) it.parentId = null;
    }
  }
}

// ── Coupe d'un mur en deux ────────────────────────────────────
/** Coupe le mur à l'abscisse u (le long de l'axe). Les ouvertures suivent leur moitié ;
 *  une ouverture à cheval sur la coupe bloque l'opération. Mur rampant : hauteurs interpolées. */
export function splitWallAt(store, floor, wall, u) {
  const len = wallLength(wall);
  if (u < 10 || u > len - 10) {
    store.getState().setStatus('Coupez plus loin des extrémités du mur');
    return;
  }
  // Une ouverture à cheval sur la coupe reste sur la moitié qui contient son centre
  // (les murs restant colinéaires, elle continue de percer les deux).
  const cut = pointOnWall(wall, u);
  const cutPt = { x: R(cut.x), y: R(cut.y) };
  const id2 = newId('w');
  store.getState().mutate((floors) => {
    const f = floors.find((x) => x.id === floor.id);
    const w1 = f.walls.find((x) => x.id === wall.id);
    if (!w1) return;
    // Hauteur au point de coupe (mur rampant)
    const hA = w1.height;
    const hB = w1.heightB;
    let hCut = null;
    if (hA !== null && hB !== null && hA !== hB) {
      hCut = R(hA + ((hB - hA) * u) / len);
    }
    const w2 = {
      ...JSON.parse(JSON.stringify(w1)),
      id: id2,
      a: cutPt,
      height: hCut ?? hA,
      heightB: hB,
    };
    w1.b = cutPt;
    if (hCut !== null) w1.heightB = hCut;
    f.walls.push(w2);
    // Réaffecte les ouvertures de la seconde moitié
    for (const op of f.openings) {
      if (op.wallId === wall.id && op.offset >= u) {
        op.wallId = id2;
        op.offset = R(op.offset - u);
      }
    }
  });
  store.getState().setSelection([{ kind: 'wall', id: wall.id }, { kind: 'wall', id: id2 }]);
  store.getState().setStatus('Mur coupé en deux');
}

// ══ Outils ═══════════════════════════════════════════════════
// Chaque outil reçoit ctx = { store, floor, settings, world, tol, shift, ctrl, ts, setUi }
// ts = état mutable de l'outil (persiste entre événements), setUi = aperçus/snap pour le rendu.

export const toolHandlers = {
  // ── Sélection / déplacement (Ctrl+clic : ajouter/retirer, glisser sur le vide : rectangle) ──
  select: {
    cursor: 'default',
    onDown(ctx) {
      const { store, floor, world, tol, ctrl, ts } = ctx;
      const sel = store.getState().selection;
      // Poignées d'extrémité quand un seul mur est sélectionné
      if (sel.length === 1 && sel[0].kind === 'wall') {
        const wall = floor.walls.find((w) => w.id === sel[0].id);
        if (wall) {
          for (const end of ['a', 'b']) {
            if (dist(world, wall[end]) <= tol) {
              store.getState().beginTransient();
              ts.drag = { mode: 'endpoint', wallId: wall.id, end };
              return;
            }
          }
        }
      }
      // Poignées de la pièce sélectionnée : courbure, coins de rect, sommets de poly
      if (sel.length === 1 && sel[0].kind === 'room') {
        const room = floor.rooms.find((r) => r.id === sel[0].id);
        if (room) {
          for (const h of bulgeHandles(room)) {
            if (dist(world, h.p) <= tol) {
              store.getState().beginTransient();
              ts.drag = { mode: 'bulge', roomId: room.id, blockIndex: h.blockIndex, edgeIndex: h.edgeIndex };
              return;
            }
          }
          for (const h of roomHandles(room)) {
            if (dist(world, h.p) <= tol) {
              store.getState().beginTransient();
              ts.drag = {
                mode: h.kind === 'rectCorner' ? 'rectCorner' : 'vertex',
                roomId: room.id, blockIndex: h.blockIndex, index: h.index,
              };
              return;
            }
          }
        }
      }
      const hit = hitAny(floor, world, tol);
      if (!hit) {
        // Rectangle de sélection sur le vide
        if (!ctrl) store.getState().setSelection([]);
        ts.rubber = { start: world };
        return;
      }
      const item = { kind: hit.kind, id: hit.id };
      const inSel = sel.some((s) => sameSel(s, item));
      if (ctrl) {
        // Ajoute / retire de la sélection, sans déplacement
        store.getState().setSelection(inSel ? sel.filter((s) => !sameSel(s, item)) : [...sel, item]);
        return;
      }
      if (inSel && sel.length > 1) {
        // Déplacement groupé de la sélection existante
        store.getState().beginTransient();
        ts.drag = { mode: 'multi', last: world };
        return;
      }
      store.getState().setSelection([item]);
      store.getState().beginTransient();
      if (hit.kind === 'wall') {
        ts.drag = { mode: 'wall', wallId: hit.id, last: world };
      } else if (hit.kind === 'opening') {
        ts.drag = hit.wall
          ? { mode: 'opening', openingId: hit.id, wallId: hit.wall.id }
          : { mode: 'velux', openingId: hit.id, last: world };
      } else if (hit.kind === 'room') {
        ts.drag = { mode: 'room', roomId: hit.id, last: world };
      } else if (hit.kind === 'stair') {
        ts.drag = { mode: 'stair', stairId: hit.id, last: world };
      } else if (hit.kind === 'item') {
        ts.drag = { mode: 'item', itemId: hit.id, last: world };
      } else if (hit.kind === 'dim' || hit.kind === 'beam') {
        ts.drag = { mode: 'item', itemId: hit.id, last: world }; // translation générique
      }
    },
    onMove(ctx) {
      const { store, floor, settings, world, tol, shift, ts, setUi } = ctx;
      if (ts.rubber) {
        setUi({ rubber: { a: ts.rubber.start, b: world } });
        return;
      }
      if (!ts.drag) {
        const hit = hitAny(floor, world, tol);
        setUi({ hover: hit ? { kind: hit.kind, id: hit.id } : null });
        return;
      }
      const d = ts.drag;
      if (d.mode === 'endpoint') {
        const { p } = snapPoint(world, { ...floor, walls: floor.walls.filter((w) => w.id !== d.wallId) }, settings, tol, null, shift);
        store.getState().applyTransient((floors) => {
          const f = floors.find((x) => x.id === floor.id);
          const wall = f.walls.find((w) => w.id === d.wallId);
          if (wall) wall[d.end] = p;
        });
        setUi({ snapPoint: p });
      } else if (d.mode === 'wall' || d.mode === 'multi') {
        const dx = world.x - d.last.x;
        const dy = world.y - d.last.y;
        d.last = world;
        const sel = store.getState().selection;
        const wallIds = d.mode === 'multi'
          ? new Set(sel.filter((s) => s.kind === 'wall').map((s) => s.id))
          : new Set([d.wallId]);
        const roomIds = d.mode === 'multi'
          ? new Set(sel.filter((s) => s.kind === 'room').map((s) => s.id))
          : new Set();
        store.getState().applyTransient((floors) => {
          const f = floors.find((x) => x.id === floor.id);
          const moved = new Set([...wallIds, ...roomIds]);
          for (const wall of f.walls) {
            if (wallIds.has(wall.id)) {
              wall.a = { x: wall.a.x + dx, y: wall.a.y + dy };
              wall.b = { x: wall.b.x + dx, y: wall.b.y + dy };
            }
          }
          for (const room of f.rooms) {
            if (roomIds.has(room.id)) translateRoom(room, dx, dy);
          }
          // Les descendants suivent leur parent (position relative constante)
          for (const id of [...wallIds, ...roomIds]) translateDescendants(f, id, dx, dy, moved);
        });
      } else if (d.mode === 'rectCorner' || d.mode === 'vertex') {
        const { p, guides } = snapRoomPoint(world, floor, settings, tol, d.roomId);
        store.getState().applyTransient((floors) => {
          const f = floors.find((x) => x.id === floor.id);
          const room = f.rooms.find((r) => r.id === d.roomId);
          const block = room?.blocks?.[d.blockIndex];
          if (!block) return;
          if (d.mode === 'rectCorner' && block.type === 'rect') {
            resizeRectBlock(block, d.index, p);
          } else if (d.mode === 'vertex' && block.type === 'poly') {
            const pt = block.points[d.index];
            pt.x = R(p.x);
            pt.y = R(p.y);
          }
          const c = roomCentroid(room);
          if (c) room.position = { x: R(c.x), y: R(c.y) };
        });
        setUi({ guides, snapPoint: p });
      } else if (d.mode === 'bulge') {
        store.getState().applyTransient((floors) => {
          const f = floors.find((x) => x.id === floor.id);
          const room = f.rooms.find((r) => r.id === d.roomId);
          const block = room?.blocks?.[d.blockIndex];
          if (!block || block.type !== 'poly') return;
          const p1 = block.points[d.edgeIndex];
          const p2 = block.points[(d.edgeIndex + 1) % block.points.length];
          let b = bulgeFromPoint(p1, p2, world);
          if (Math.abs(b) < 0.06) b = 0; // aimantation au côté droit
          p1.bulge = Math.max(-1.5, Math.min(1.5, b));
        });
      } else if (d.mode === 'opening') {
        store.getState().applyTransient((floors) => {
          const f = floors.find((x) => x.id === floor.id);
          const op = f.openings.find((o) => o.id === d.openingId);
          const wall = f.walls.find((w) => w.id === d.wallId);
          if (op && wall) {
            const u = placementU(wall, f.walls, projectU(wall, world), op.width);
            if (u !== null) op.offset = R(u);
            // Cotes en direct pendant le glissement
            const len = wallLength(wall);
            store.getState().setStatus(
              `bord ↔ a : ${R(op.offset - op.width / 2)} cm — bord ↔ b : ${R(len - op.offset - op.width / 2)} cm`,
            );
          }
        });
      } else if (d.mode === 'room' || d.mode === 'stair' || d.mode === 'velux' || d.mode === 'item') {
        const dx = world.x - d.last.x;
        const dy = world.y - d.last.y;
        d.last = world;
        const movedId = d.roomId ?? d.stairId ?? d.openingId ?? d.itemId;
        store.getState().applyTransient((floors) => {
          const f = floors.find((x) => x.id === floor.id);
          translateItem(f, movedId, dx, dy);
          translateDescendants(f, movedId, dx, dy);
        });
      }
    },
    onUp(ctx) {
      const { store, floor, settings, world, ctrl, ts, setUi } = ctx;
      if (ts.rubber) {
        const found = itemsInRect(floor, ts.rubber.start, world);
        const sel = store.getState().selection;
        const merged = ctrl
          ? [...sel, ...found.filter((f) => !sel.some((s) => sameSel(s, f)))]
          : found;
        store.getState().setSelection(merged);
        ts.rubber = null;
        setUi({ rubber: null, status: merged.length ? `${merged.length} élément(s) sélectionné(s) — Suppr pour supprimer` : '' });
        return;
      }
      if (ts.drag) {
        // Arrondi final à la grille pour les déplacements de murs/pièces
        const d = ts.drag;
        if ((d.mode === 'wall' || d.mode === 'multi' || d.mode === 'room') && settings.grid.snap) {
          const sel = store.getState().selection;
          const wallIds = d.mode === 'multi'
            ? new Set(sel.filter((s) => s.kind === 'wall').map((s) => s.id))
            : new Set(d.mode === 'wall' ? [d.wallId] : []);
          const roomIds = d.mode === 'multi'
            ? new Set(sel.filter((s) => s.kind === 'room').map((s) => s.id))
            : new Set(d.mode === 'room' ? [d.roomId] : []);
          store.getState().applyTransient((floors) => {
            const f = floors.find((x) => x.id === floor.id);
            const moved = new Set([...wallIds, ...roomIds]);
            for (const wall of f.walls) {
              if (wallIds.has(wall.id)) {
                const before = { ...wall.a };
                wall.a = snapToGrid(wall.a, settings.grid.size);
                wall.b = snapToGrid(wall.b, settings.grid.size);
                // Le delta d'arrondi se propage aux descendants
                translateDescendants(f, wall.id, wall.a.x - before.x, wall.a.y - before.y, moved);
              }
            }
            for (const room of f.rooms) {
              if (roomIds.has(room.id)) {
                const before = { ...room.position };
                snapRoomToGrid(room, settings.grid.size);
                translateDescendants(f, room.id, room.position.x - before.x, room.position.y - before.y, moved);
              }
            }
          });
        }
        store.getState().commitTransient();
        ts.drag = null;
        setUi({ snapPoint: null, guides: null });
      }
    },
    /** Double-clic : supprimer un sommet, ajouter un sommet sur un côté, ou renommer la pièce */
    onDbl(ctx) {
      const { store, floor, world, tol } = ctx;
      const sel = store.getState().selection;
      if (sel.length === 1 && sel[0].kind === 'room') {
        const room = floor.rooms.find((r) => r.id === sel[0].id);
        if (room) {
          // 1. Sommet de polygone → suppression (minimum 3)
          for (const h of roomHandles(room)) {
            if (h.kind === 'vertex' && dist(world, h.p) <= tol) {
              const block = room.blocks[h.blockIndex];
              if (block.points.length <= 3) return;
              store.getState().mutate((floors) => {
                const r = floors.find((x) => x.id === floor.id).rooms.find((x) => x.id === room.id);
                r.blocks[h.blockIndex].points.splice(h.index, 1);
              });
              return;
            }
          }
          // 2. Côté de polygone → insertion d'un sommet (test sur clone avant de muter)
          const probe = JSON.parse(JSON.stringify(room));
          if (insertVertexNear(probe, world, tol)) {
            store.getState().mutate((floors) => {
              const r = floors.find((x) => x.id === floor.id).rooms.find((x) => x.id === room.id);
              insertVertexNear(r, world, tol);
            });
            return;
          }
        }
      }
      // 3. Double-clic sur un mur → le couper en deux à cet endroit
      const hit = hitAny(floor, world, tol);
      if (hit?.kind === 'wall') {
        splitWallAt(store, floor, hit.wall, hit.u);
        return;
      }
      // 4. Double-clic sur une pièce → renommage
      if (hit?.kind === 'room') {
        const name = window.prompt('Nom de la pièce :', hit.room.name);
        if (name?.trim()) {
          store.getState().mutate((floors) => {
            const r = floors.find((x) => x.id === floor.id).rooms.find((x) => x.id === hit.id);
            if (r) r.name = name.trim();
          });
        }
      }
    },
    onKey(ctx, key) {
      if (key === 'Delete' || key === 'Backspace') {
        deleteSelection(ctx.store, ctx.floor.id);
        return;
      }
      // R : tourne les objets sélectionnés de 90°, ou l'orientation des ouvertures
      if (key.toLowerCase() === 'r') {
        const { store, floor } = ctx;
        const sel = store.getState().selection;
        const itemIds = new Set(sel.filter((s) => s.kind === 'item').map((s) => s.id));
        if (itemIds.size) {
          store.getState().mutate((floors) => {
            const f = floors.find((x) => x.id === floor.id);
            for (const it of f.items ?? []) {
              if (itemIds.has(it.id)) it.rotation = ((it.rotation ?? 0) + 90) % 360;
            }
          });
        }
        const doorIds = new Set(sel.filter((s) => s.kind === 'opening').map((s) => s.id));
        if (!doorIds.size) return;
        store.getState().mutate((floors) => {
          const f = floors.find((x) => x.id === floor.id);
          for (const op of f.openings) {
            if (!doorIds.has(op.id) || !op.wallId) continue;
            // Cycle : normal → battant inversé → les deux → autre côté → normal
            const idx = (op.meta?.flipH ? 1 : 0) + (op.meta?.flipV ? 2 : 0);
            const next = [1, 3, 0, 2][idx];
            op.meta = { ...op.meta, flipH: !!(next & 1), flipV: !!(next & 2) };
          }
        });
      }
    },
  },

  // ── Tracé de murs (clic-clic en chaîne) ──
  wall: {
    cursor: 'crosshair',
    onDown(ctx) {
      const { store, floor, settings, world, tol, shift, ts } = ctx;
      const { p } = snapPoint(world, floor, settings, tol, ts.start, shift);
      if (!ts.start) {
        ts.start = p;
        return;
      }
      if (dist(ts.start, p) < 1) return;
      const start = ts.start;
      // Origine : le mur est rattaché à la dalle qui contient son point de départ
      const hostSlab = floor.rooms.find((r) => r.kind === 'slab' && r.blocks?.length
        && pointInRoom(r, start));
      store.getState().mutate((floors, project) => {
        const f = floors.find((x) => x.id === floor.id);
        f.walls.push({
          id: newId('w'),
          a: { x: R(start.x), y: R(start.y) },
          b: { x: R(p.x), y: R(p.y) },
          thickness: project.settings.defaultWallThickness,
          height: null,
          parentId: hostSlab?.id ?? null,
        });
      });
      ts.start = p; // chaînage
    },
    onMove(ctx) {
      const { floor, settings, world, tol, shift, ts, setUi } = ctx;
      const { p, kind } = snapPoint(world, floor, settings, tol, ts.start, shift);
      ts.hover = p;
      setUi({
        snapPoint: kind === 'endpoint' ? p : null,
        preview: ts.start ? { type: 'wall', a: ts.start, b: p, thickness: settings.defaultWallThickness } : null,
        status: ts.start ? `Longueur : ${R(dist(ts.start, p))} cm — Échap pour terminer` : 'Cliquez pour démarrer un mur',
      });
    },
    onKey(ctx, key) {
      if (key === 'Escape') {
        ctx.ts.start = null;
        ctx.setUi({ preview: null, status: '' });
      }
    },
    onCancel(ctx) { // clic droit
      ctx.ts.start = null;
      ctx.setUi({ preview: null, status: '' });
    },
  },

  // ── Charpente : poutres (clic-clic en chaîne) et poteaux (clic) ──
  beam: {
    cursor: 'crosshair',
    onDown(ctx) {
      const { store, floor, settings, world, tol, shift, ts } = ctx;
      const kind = store.getState().beamKind;
      const { p } = snapPoint(world, floor, settings, tol, ts.beamStart, shift);
      if (kind === 'post') {
        store.getState().mutate((floors) => {
          const f = floors.find((x) => x.id === floor.id);
          if (!f.beams) f.beams = [];
          f.beams.push({
            kind: 'post', id: newId('bm'), p: { x: R(p.x), y: R(p.y) },
            w: 15, d: null, height: null, elevation: 0, material: 'wood',
            folderId: null, parentId: null, hidden: false,
          });
        });
        return;
      }
      if (!ts.beamStart) {
        ts.beamStart = p;
        return;
      }
      if (dist(ts.beamStart, p) < 1) return;
      const start = ts.beamStart;
      store.getState().mutate((floors) => {
        const f = floors.find((x) => x.id === floor.id);
        if (!f.beams) f.beams = [];
        f.beams.push({
          kind: 'beam', id: newId('bm'),
          a: { x: R(start.x), y: R(start.y) }, b: { x: R(p.x), y: R(p.y) },
          w: 15, h: 20, elevA: null, elevB: null, material: 'wood',
          folderId: null, parentId: null, hidden: false,
        });
      });
      ts.beamStart = p; // chaînage (lisses, pannes, pergola…)
    },
    onMove(ctx) {
      const { store, floor, settings, world, tol, shift, ts, setUi } = ctx;
      const kind = store.getState().beamKind;
      const { p, kind: snapKind } = snapPoint(world, floor, settings, tol, ts.beamStart, shift);
      if (kind === 'post') {
        setUi({
          preview: { type: 'post', p, w: 15 },
          snapPoint: snapKind === 'endpoint' ? p : null,
          status: 'Cliquez pour poser un poteau (section et hauteur dans le panneau propriétés)',
        });
        return;
      }
      setUi({
        snapPoint: snapKind === 'endpoint' ? p : null,
        preview: ts.beamStart ? { type: 'beam', a: ts.beamStart, b: p, w: 15 } : null,
        status: ts.beamStart
          ? `Poutre : ${fmtLen(dist(ts.beamStart, p))} cm — Échap pour terminer`
          : 'Cliquez pour démarrer une poutre (linteau, panne, chevron…)',
      });
    },
    onKey(ctx, key) {
      if (key === 'Escape') {
        ctx.ts.beamStart = null;
        ctx.setUi({ preview: null, status: '' });
      }
    },
    onCancel(ctx) {
      ctx.ts.beamStart = null;
      ctx.setUi({ preview: null, status: '' });
    },
  },

  // ── Cotes manuelles : deux clics = une mesure persistée ──
  measure: {
    cursor: 'crosshair',
    onDown(ctx) {
      const { store, floor, settings, world, tol, ts, setUi } = ctx;
      const { p } = snapRoomPoint(world, floor, settings, tol);
      if (!ts.measureA) {
        ts.measureA = { x: R(p.x), y: R(p.y) };
        return;
      }
      const a = ts.measureA;
      const b = { x: R(p.x), y: R(p.y) };
      ts.measureA = null;
      setUi({ preview: null, status: '' });
      if (dist(a, b) < 2) return;
      store.getState().mutate((floors) => {
        const f = floors.find((x) => x.id === floor.id);
        if (!f.dims) f.dims = [];
        f.dims.push({ id: newId('m'), a, b, folderId: null, parentId: null, hidden: false });
      });
    },
    onMove(ctx) {
      const { floor, settings, world, tol, ts, setUi } = ctx;
      const { p, guides, snapped } = snapRoomPoint(world, floor, settings, tol);
      if (ts.measureA) {
        setUi({
          preview: { type: 'measure', a: ts.measureA, b: p },
          guides,
          status: `${R(dist(ts.measureA, p))} cm — cliquez le second point (Échap pour annuler)`,
        });
      } else {
        setUi({ guides, snapPoint: snapped ? p : null, status: 'Cliquez le premier point de la mesure' });
      }
    },
    onKey(ctx, key) {
      if (key === 'Escape') {
        ctx.ts.measureA = null;
        ctx.setUi({ preview: null, guides: null, status: '' });
      }
    },
    onCancel(ctx) { this.onKey(ctx, 'Escape'); },
  },

  // ── Pose d'objets de la bibliothèque ──
  item: {
    cursor: 'copy',
    onMove(ctx) {
      const { store, settings, world, ts, setUi } = ctx;
      const state = store.getState();
      const entry = findCatalogItem(state.catalog, state.placingId);
      if (!entry) {
        setUi({ preview: null, status: 'Choisissez un objet dans la bibliothèque (panneau de gauche)' });
        return;
      }
      const p = settings.grid.snap ? snapToGrid(world, settings.grid.size) : world;
      setUi({
        preview: { type: 'item', entry, position: p, rotation: ts.itemRot ?? 0 },
        status: `${entry.name} — clic pour poser, R pour tourner (${ts.itemRot ?? 0}°)`,
      });
    },
    onDown(ctx) {
      const { store, floor, settings, world, ts } = ctx;
      const state = store.getState();
      const entry = findCatalogItem(state.catalog, state.placingId);
      if (!entry) return;
      const p = settings.grid.snap ? snapToGrid(world, settings.grid.size) : world;
      store.getState().mutate((floors) => {
        const f = floors.find((x) => x.id === floor.id);
        if (!f.items) f.items = [];
        f.items.push({
          id: newId('i'), catalogId: entry.id,
          position: { x: R(p.x), y: R(p.y) },
          rotation: ts.itemRot ?? 0, elevation: null, scale: 1, label: null,
          folderId: null, parentId: null, hidden: false,
        });
      });
    },
    onKey(ctx, key) {
      if (key.toLowerCase() === 'r') {
        ctx.ts.itemRot = ((ctx.ts.itemRot ?? 0) + 90) % 360;
      } else if (key === 'Escape') {
        ctx.setUi({ preview: null, status: '' });
      }
    },
  },

  // ── Pose d'ouvertures ──
  door: makeOpeningTool('door'),
  window: makeOpeningTool('window'),
  garage: makeOpeningTool('garage'),
  passage: makeOpeningTool('passage'),

  // ── Escaliers : droit/à angles et échelle = clics du chemin ; colimaçon = centre puis rayon ──
  stair: {
    cursor: 'crosshair',
    onDown(ctx) {
      const { store, floor, settings, world, tol, ts } = ctx;
      const p = settings.grid.snap ? snapToGrid(world, settings.grid.size) : world;
      const kind = store.getState().stairKind;
      if (kind === 'spiral') {
        if (!ts.spiralCenter) {
          ts.spiralCenter = { x: R(p.x), y: R(p.y) };
          return;
        }
        const radius = Math.max(40, R(dist(ts.spiralCenter, world)));
        const center = ts.spiralCenter;
        ts.spiralCenter = null;
        ctx.setUi({ preview: null, status: '' });
        store.getState().mutate((floors) => {
          const f = floors.find((x) => x.id === floor.id);
          if (!f.stairs) f.stairs = [];
          f.stairs.push({ id: newId('s'), kind: 'spiral', center, radius, startAngle: 0, sweep: 360, folderId: null, hidden: false });
        });
        return;
      }
      if (!ts.stairPath) ts.stairPath = [];
      const last = ts.stairPath[ts.stairPath.length - 1];
      const pt = { x: R(p.x), y: R(p.y) };
      if (!last || dist(last, pt) > 1) ts.stairPath.push(pt);
    },
    onMove(ctx) {
      const { store, settings, world, ts, setUi } = ctx;
      const p = settings.grid.snap ? snapToGrid(world, settings.grid.size) : world;
      const kind = store.getState().stairKind;
      const width = kind === 'ladder' ? 60 : 90;
      if (kind === 'spiral') {
        if (ts.spiralCenter) {
          const radius = Math.max(40, R(dist(ts.spiralCenter, world)));
          setUi({
            preview: { type: 'stair', stair: { kind: 'spiral', center: ts.spiralCenter, radius, startAngle: 0, sweep: 360 } },
            status: `Rayon : ${radius} cm — cliquez pour valider`,
          });
        } else {
          setUi({ status: 'Cliquez le centre du colimaçon' });
        }
      } else if (ts.stairPath?.length) {
        setUi({
          preview: { type: 'stair', stair: { kind, path: [...ts.stairPath, p], width } },
          status: 'Cliquez les points du parcours (bas → haut) — clic droit, double-clic ou Entrée pour TERMINER, Échap pour annuler',
        });
      } else {
        setUi({ status: 'Cliquez le départ de l\'escalier (en bas)' });
      }
    },
    /** Termine le tracé (≥ 2 points) */
    finish(ctx) {
      const { store, floor, ts, setUi } = ctx;
      const kind = store.getState().stairKind;
      const path = (ts.stairPath ?? []).filter((p, i, arr) => i === 0 || dist(p, arr[i - 1]) > 1);
      ts.stairPath = null;
      setUi({ preview: null, status: '' });
      if (path.length < 2) return;
      store.getState().mutate((floors) => {
        const f = floors.find((x) => x.id === floor.id);
        if (!f.stairs) f.stairs = [];
        f.stairs.push({
          id: newId('s'), kind, path, width: kind === 'ladder' ? 60 : 90,
          folderId: null, parentId: null, hidden: false,
        });
      });
    },
    onKey(ctx, key) {
      const { ts, setUi } = ctx;
      if (key === 'Escape') {
        ts.stairPath = null;
        ts.spiralCenter = null;
        setUi({ preview: null, status: '' });
        return;
      }
      if (key === 'Enter' && ts.stairPath?.length >= 2) this.finish(ctx);
    },
    onDbl(ctx) {
      if (ctx.ts.stairPath?.length >= 2) this.finish(ctx);
    },
    // Clic droit : TERMINE le tracé s'il est valide, sinon annule
    onCancel(ctx) {
      if (ctx.ts.stairPath?.length >= 2) this.finish(ctx);
      else this.onKey(ctx, 'Escape');
    },
  },

  // ── Pièces et dalles : deux sous-outils (store.roomMode) ──
  // 'rect' : glisser un rectangle. 'poly' : cliquer les sommets, Entrée/1er point pour fermer.
  // Un bloc démarré à l'intérieur d'une forme existante du même type s'y ajoute (L, T…).
  room: makeRoomTool('room'),
  slab: makeRoomTool('slab'),
  roof: makeRoomTool('roof'),

  // ── Suppression directe ──
  delete: {
    cursor: 'not-allowed',
    onDown(ctx) {
      const { store, floor, world, tol } = ctx;
      const hit = hitAny(floor, world, tol);
      if (!hit) return;
      store.getState().mutate((floors) => {
        const f = floors.find((x) => x.id === floor.id);
        if (hit.kind === 'wall') {
          f.walls = f.walls.filter((w) => w.id !== hit.id);
          f.openings = f.openings.filter((o) => o.wallId !== hit.id);
        } else if (hit.kind === 'opening') {
          f.openings = f.openings.filter((o) => o.id !== hit.id);
        } else if (hit.kind === 'room') {
          f.rooms = f.rooms.filter((r) => r.id !== hit.id);
        }
      });
    },
    onMove(ctx) {
      const hit = hitAny(ctx.floor, ctx.world, ctx.tol);
      ctx.setUi({ hover: hit ? { kind: hit.kind, id: hit.id } : null, status: 'Cliquez sur un élément pour le supprimer' });
    },
  },

  // ── Origine du projet : cliquer un point (accroché aux coins/extrémités) qui
  // devient le (0,0). TOUT le plan (tous les étages) est translaté ; les X/Y du
  // panneau deviennent relatifs à ce repère. La vue est décalée d'autant pour que
  // le plan ne saute pas à l'écran (le point cliqué reste sous le curseur).
  origin: {
    cursor: 'crosshair',
    onMove(ctx) {
      const { world, floor, settings, tol, setUi } = ctx;
      if (!floor) return;
      const { p } = snapPoint(world, floor, settings, tol, null, false);
      setUi({ snapPoint: p, status: `Nouveau (0,0) → ${Math.round(p.x)}, ${Math.round(p.y)} cm — cliquez pour fixer l'origine (Échap annule)` });
    },
    onDown(ctx) {
      const { world, floor, settings, tol, store, view, setUi } = ctx;
      if (!floor) return;
      const { p } = snapPoint(world, floor, settings, tol, null, false);
      const ox = roundCoord(p.x);
      const oy = roundCoord(p.y);
      if (ox === 0 && oy === 0) {
        setUi({ snapPoint: null, status: 'Ce point est déjà l\'origine.' });
        store.getState().setTool('select');
        return;
      }
      store.getState().mutate((floors) => {
        for (const f of floors) shiftFloorCoords(f, ox, oy);
      });
      // Garder le plan immobile : le point cliqué (désormais 0,0) reste sous le curseur.
      if (view) { view.panX += ox * view.zoom; view.panY += oy * view.zoom; }
      setUi({ snapPoint: null, status: 'Origine du projet redéfinie sur le point cliqué.' });
      store.getState().setTool('select');
    },
    onCancel(ctx) { ctx.setUi({ snapPoint: null, status: '' }); ctx.store.getState().setTool('select'); },
    onKey(ctx, key) {
      if (key === 'Escape') { ctx.setUi({ snapPoint: null, status: '' }); ctx.store.getState().setTool('select'); }
    },
  },
};

/** Outil Pièce/Dalle/Toit paramétré par le kind de la forme créée */
function makeRoomTool(kind) {
  const noun = { slab: 'dalle', roof: 'toit', room: 'pièce' }[kind];
  return {
    cursor: 'crosshair',
    onDown(ctx) {
      const { store, floor, settings, world, tol, ts } = ctx;
      const { p } = snapRoomPoint(world, floor, settings, tol);
      if (store.getState().roomMode === 'rect') {
        ts.downAt = p;
      } else {
        if (!ts.polyPoints) ts.polyPoints = [];
        if (ts.polyPoints.length >= 3 && dist(world, ts.polyPoints[0]) <= tol) {
          closePoly(ctx, kind);
          return;
        }
        ts.polyPoints.push({ x: R(p.x), y: R(p.y) });
      }
    },
    onMove(ctx) {
      const { store, floor, settings, world, tol, ts, setUi } = ctx;
      const { p, guides, snapped } = snapRoomPoint(world, floor, settings, tol);
      if (store.getState().roomMode === 'rect') {
        if (ts.downAt) {
          setUi({
            preview: { type: 'roomRect', a: ts.downAt, b: p },
            guides,
            status: `${R(Math.abs(p.x - ts.downAt.x))} × ${R(Math.abs(p.y - ts.downAt.y))} cm — relâchez pour créer`,
          });
        } else {
          setUi({ guides, snapPoint: snapped ? p : null, status: `Glissez pour tracer un bloc rectangulaire (sur une ${noun} : ajoute un bloc)` });
        }
      } else if (ts.polyPoints?.length) {
        const closable = ts.polyPoints.length >= 3 && dist(world, ts.polyPoints[0]) <= tol;
        const last = ts.polyPoints[ts.polyPoints.length - 1];
        setUi({
          preview: { type: 'roomPoly', points: ts.polyPoints, cursor: closable ? ts.polyPoints[0] : p },
          guides,
          status: closable
            ? 'Cliquez le premier point (ou Entrée) pour fermer'
            : `Segment : ${R(dist(last, p))} cm — Entrée pour fermer, Échap pour annuler`,
        });
      } else {
        setUi({ guides, snapPoint: snapped ? p : null, status: `Cliquez les sommets du polygone (sur une ${noun} : ajoute un bloc)` });
      }
    },
    onUp(ctx) {
      const { store, floor, settings, world, tol, ts, setUi } = ctx;
      if (store.getState().roomMode !== 'rect' || !ts.downAt) return;
      const { p } = snapRoomPoint(world, floor, settings, tol);
      const x = R(Math.min(ts.downAt.x, p.x));
      const y = R(Math.min(ts.downAt.y, p.y));
      const w = R(Math.abs(p.x - ts.downAt.x));
      const h = R(Math.abs(p.y - ts.downAt.y));
      const start = ts.downAt;
      ts.downAt = null;
      setUi({ preview: null, guides: null, status: '' });
      if (w >= 20 && h >= 20) {
        commitBlock(store, floor, { type: 'rect', x, y, w, h, radius: 0 }, start, kind);
      }
    },
    onKey(ctx, key) {
      const { ts, setUi } = ctx;
      if (key === 'Escape') {
        ts.polyPoints = null;
        ts.downAt = null;
        setUi({ preview: null, guides: null, status: '' });
      } else if (key === 'Enter' && ts.polyPoints?.length >= 3) {
        closePoly(ctx, kind);
      }
    },
    onDbl(ctx) {
      if (ctx.ts.polyPoints?.length >= 3) closePoly(ctx, kind);
    },
    // Clic droit : ferme le polygone s'il est valide, sinon annule
    onCancel(ctx) {
      if (ctx.ts.polyPoints?.length >= 3) closePoly(ctx, kind);
      else this.onKey(ctx, 'Escape');
    },
  };
}

const TYPE_LABELS = { door: 'porte', window: 'fenêtre', garage: 'porte de garage', passage: 'ouverture libre' };

function makeOpeningTool(type) {
  return {
    cursor: 'copy',
    onMove(ctx) {
      const { store, floor, settings, world, tol, setUi } = ctx;
      const variant = store.getState().openingVariants[type];
      const def = openingDefaults(type, variant);
      // Velux : se pose n'importe où sur le plan (toit), pas sur un mur
      if (type === 'window' && variant === 'velux') {
        const p = settings.grid.snap ? snapToGrid(world, settings.grid.size) : world;
        setUi({
          preview: { type: 'velux', position: p, width: def.width, height: def.height },
          status: 'Cliquez pour poser le velux (fenêtre de toit)',
        });
        return;
      }
      const hit = hitWall(floor.walls.filter((w) => !w.hidden), world, tol * 2);
      if (!hit) {
        setUi({ preview: null, status: `Survolez un mur pour poser une ${TYPE_LABELS[type]}` });
        return;
      }
      const len = wallLength(hit.wall);
      const u = placementU(hit.wall, floor.walls, projectU(hit.wall, world), def.width);
      if (u === null) {
        setUi({ preview: null, status: 'Mur trop court pour cette ouverture' });
        return;
      }
      // Cotes en direct : distance des bords de l'ouverture aux extrémités du mur
      const dA = R(u - def.width / 2);
      const dB = R(len - (u + def.width / 2));
      setUi({
        preview: { type: 'opening', wall: hit.wall, u, width: def.width, kind: type },
        status: `bord ↔ extrémité a : ${dA} cm — bord ↔ extrémité b : ${dB} cm`,
      });
    },
    onDown(ctx) {
      const { store, floor, settings, world, tol } = ctx;
      const variant = store.getState().openingVariants[type];
      const def = openingDefaults(type, variant);
      if (type === 'window' && variant === 'velux') {
        const p = settings.grid.snap ? snapToGrid(world, settings.grid.size) : world;
        store.getState().mutate((floors) => {
          const f = floors.find((x) => x.id === floor.id);
          f.openings.push({
            id: newId('o'), type, variant, wallId: null,
            position: { x: R(p.x), y: R(p.y) },
            offset: 0, ...def, meta: {}, folderId: null, hidden: false,
          });
        });
        return;
      }
      const hit = hitWall(floor.walls.filter((w) => !w.hidden), world, tol * 2);
      if (!hit) return;
      const u = placementU(hit.wall, floor.walls, projectU(hit.wall, world), def.width);
      if (u === null) return;
      store.getState().mutate((floors) => {
        const f = floors.find((x) => x.id === floor.id);
        f.openings.push({
          id: newId('o'), type, variant, wallId: hit.wall.id, offset: R(u),
          ...def, meta: {}, folderId: null, hidden: false,
        });
      });
    },
  };
}

/**
 * Abscisse de pose d'une ouverture : autorise le débordement sur un mur COLINÉAIRE joint
 * (fenêtre à cheval sur deux murs) ; sinon clampe dans le mur ; null si impossible.
 */
function placementU(wall, walls, uRaw, width) {
  const len = wallLength(wall);
  const covered = (u) => openingCovered({ offset: u, width }, wall, walls);
  if (covered(uRaw)) return uRaw;
  const clamped = Math.max(width / 2, Math.min(len - width / 2, uRaw));
  if (len >= width) return clamped;
  return covered(clamped) ? clamped : null;
}
